import capitalize from 'lodash-es/capitalize';

import { ActionVariant, getActionVariantName } from './components/Action';
import {
  ALLOW_BACKGROUND_COLORS,
  BackgroundClassMap,
  COLOR_NAME_BRAND,
} from './constants/colors';
import { IconNames } from './constants/icons';

// Builder.io evaluate showIf in the browser with eval
// causing isHidden undefined because it defines outside
// the code that send to Builder.io in Browser.
const BaseBackground = (
  allowBackgroundColors: string[] = ALLOW_BACKGROUND_COLORS
) => ({
  name: 'backgroundColorClassName',
  friendlyName: 'Background Color',
  type: 'string',
  enum: allowBackgroundColors.map((name) => ({
    label: capitalize(name),
    value: BackgroundClassMap[name],
  })),
});

interface BackgroundColorOptions {
  isHidden?: boolean;
  allowBackgroundColors?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  override?: any;
}

export const backgroundColorInput = (
  defaultBackgroundColor: string,
  options?: BackgroundColorOptions
) =>
  options?.isHidden
    ? {
        ...BaseBackground(options.allowBackgroundColors),
        defaultValue: BackgroundClassMap[defaultBackgroundColor],
        showIf: `false`,
        ...options.override,
      }
    : {
        ...BaseBackground(options?.allowBackgroundColors),
        defaultValue: BackgroundClassMap[defaultBackgroundColor],
        showIf: `true`,
        ...options?.override,
      };

export const contentMobileBehaviorInput = () => ({
  name: 'contentMobileBehavior',
  friendlyName: 'Content behaviour on mobile',
  type: 'string',
  defaultValue: 'scroll',
  enum: ['scroll', 'swipe'],
});

interface ActionButtonInputParams {
  allowVariants?: ActionVariant[];
  allowColors?: string[];
  showColorPicker?: boolean;
  useCatawikiPlainEditor?: boolean;
}

export const actionButtonInput = ({
  allowVariants = Object.values(ActionVariant),
  allowColors = [COLOR_NAME_BRAND],
  showColorPicker = false,
  useCatawikiPlainEditor = true,
}: ActionButtonInputParams) => [
  {
    name: 'actionVariant',
    friendlyName: 'Button Type',
    type: 'string',
    defaultValue: ActionVariant.NONE.toString(),
    enum: allowVariants.map((name) => ({
      label: getActionVariantName(name),
      value: name,
    })),
  },
  {
    name: 'action',
    friendlyName: 'Button',
    type: 'object',
    defaultValue: {
      label: 'Submit',
      url: 'https://www.catawiki.com',
    },
    showIf: `options.get('actionVariant') !== 'none'`,
    subFields: [
      {
        name: 'label',
        friendlyName: 'Label',
        type: useCatawikiPlainEditor
          ? 'CatawikiPlainEditor'
          : 'CatawikiPlainEditorWithoutBuiltInTranslation',
        defaultValue: 'Submit',
        localized: true,
      },
      {
        name: 'url',
        friendlyName: 'URL',
        type: useCatawikiPlainEditor
          ? 'CatawikiPlainEditor'
          : 'CatawikiPlainEditorWithoutBuiltInTranslation',
        defaultValue: 'https://www.catawiki.com',
        localized: true,
      },
      {
        name: 'colorName',
        friendlyName: 'Color',
        type: 'string',
        defaultValue: COLOR_NAME_BRAND,
        enum: allowColors,
        showIf: `${showColorPicker}`,
      },
    ],
  },
];

export const componentLinkIdInput = () => ({
  name: 'componentLinkId',
  friendlyName: 'Section id for scrolling to this section',
  type: 'string',
  helperText:
    'This section id is for link in the page that will make page scroll to. Use a ' +
    'name without spaces, e.g., collection1, danielAbtXCatawiki. Use #collection1 ' +
    'or #danielAbtXCatawiki in the link input.',
});

export const iconInput = (name = 'icon', friendlyName = 'Icon') => ({
  name,
  friendlyName,
  type: 'string',
  helperText: 'Icon name from Catawiki Icon Library',
  enum: [
    { label: 'No Icon', value: 'no-icon' },
    ...IconNames.map((iconName) => ({
      label: iconName,
      value: iconName,
    })),
  ],
});
